import React, { useCallback, useMemo, useState } from 'react';

import secureAxios from '../../../../core/api/client/secureAxios';

import { SUBCATEGORIES } from '../../common/categories';
import { AZURE_CONNECTORS_FIELDS, NEW_AZURE_CONNECTOR_FIELDS } from '../../common/fields';
import * as actions from '../../containers/Integrations/integrationsActions';

import ArrayContainer from '../Controls/ArrayContainer/ArrayContainer';
import Button from '../Controls/Button/Button';
import Label from '../Controls/Label/Label';
import Description from '../Controls/Description/Description';
import AzureConnector from './NewAzureConnector';

import styles from './NewAzureConnector.module.css';
import ConventionCreator from '../CustomInsight/ConventionCreator';
import { CONDITIONS_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { useGetTabDisplay } from '../../hooks/lockedFeatures/useTabDisplay';

const { NEW_AZURE_CONNECTORS } = SUBCATEGORIES;
const { NEW_AZURE_AD_CONNECTOR, AZURE_AD_NAMING_CONVENTIONS } = AZURE_CONNECTORS_FIELDS;
const { TENANT_ID, TENANT_NAME, CLIENT_ID, CLIENT_SECRET } = NEW_AZURE_CONNECTOR_FIELDS;

const DEFAULT_SYNC_DETAILS = { isDuringSync: false, isError: false, tenantId: '', index: -1 };

const AzureConnectors = ({ fields, dispatch, authorizePermission }) => {
    const [syncDetails, setSyncDetails] = useState({ ...DEFAULT_SYNC_DETAILS });
    const { enabled } = useGetTabDisplay(NEW_AZURE_CONNECTORS.ID);
    const disabled = useMemo(() => !authorizePermission || !enabled, [authorizePermission, enabled]);

    const azureConnectors = fields[NEW_AZURE_AD_CONNECTOR.ID];
    const namingConventions = fields[AZURE_AD_NAMING_CONVENTIONS.ID];

    const dispatchAddAzureConnector = useCallback(() => {
        dispatch({ type: actions.ADD_NEW_AZURE_CONNECTOR, payload: { tabKey: NEW_AZURE_CONNECTORS.ID } });
    }, [dispatch]);

    const dispatchAddNamingConvention = useCallback(() => {
        dispatch({ type: actions.ADD_NAMING_CONVENTION, payload: { tabKey: NEW_AZURE_CONNECTORS.ID } });
    }, [dispatch]);

    const dispatchUpdateArray = useCallback(
        (fieldId, index, arrFieldId, value) => {
            setSyncDetails({ ...DEFAULT_SYNC_DETAILS });
            dispatch({
                type: actions.UPDATE_CONNECTOR,
                payload: { tabKey: NEW_AZURE_CONNECTORS.ID, fieldId, index, arrFieldId, value }
            });
        },
        [dispatch, setSyncDetails]
    );

    const dispatchRemove = useCallback(
        (fieldId, index) => {
            if (fieldId === NEW_AZURE_AD_CONNECTOR.ID) {
                dispatch({ type: actions.REMOVE_NEW_AZURE_CONNECTOR, payload: index });
            } else {
                dispatch({
                    type: actions.REMOVE_NAMING_CONVENTION,
                    payload: { index, tabKey: NEW_AZURE_CONNECTORS.ID }
                });
            }
        },
        [dispatch]
    );

    const syncAzureConnectorPermissions = async (azureConnector, index) => {
        let newRoles = [];
        const tenantId = azureConnector[TENANT_ID.ID].value;
        const newSyncDetails = { ...DEFAULT_SYNC_DETAILS, isDuringSync: true, index, tenantId: tenantId };
        try {
            setSyncDetails({ ...newSyncDetails });
            const { data } = await secureAxios.post('/Settings/syncAzureConnectorPermissions', {
                tenantId: tenantId,
                tenantName: azureConnector[TENANT_NAME.ID].value,
                clientId: azureConnector[CLIENT_ID.ID].value,
                clientSecret: azureConnector[CLIENT_SECRET.ID].value
            });
            newRoles = data.roles;
        } catch (err) {
            newSyncDetails.isError = true;
        } finally {
            setSyncDetails({ ...newSyncDetails, isDuringSync: false });
            dispatch({
                type: actions.UPDATE_CONNECTOR,
                payload: {
                    tabKey: NEW_AZURE_CONNECTORS.ID,
                    fieldId: NEW_AZURE_AD_CONNECTOR.ID,
                    index,
                    arrFieldId: NEW_AZURE_CONNECTOR_FIELDS.ROLES.ID,
                    value: newRoles
                }
            });
        }
    };

    return (
        <>
            {azureConnectors && (
                <div className={styles.azureConnectors}>
                    <Label value={azureConnectors.name} />
                    {azureConnectors.desc && <Description value={azureConnectors.desc} expendDescription={true} />}
                    {azureConnectors.value.map((azureConnector, index) => {
                        return (
                            <div key={NEW_AZURE_AD_CONNECTOR.ID + index} className={styles.azureConnectorsContainer}>
                                <ArrayContainer
                                    key={NEW_AZURE_AD_CONNECTOR.ID + index}
                                    header="Entra ID Connector"
                                    topSpace={false}
                                    remove={() => !disabled && dispatchRemove(NEW_AZURE_AD_CONNECTOR.ID, index)}>
                                    <AzureConnector
                                        azureConnector={azureConnector}
                                        syncError={syncDetails.isError}
                                        showSyncText={
                                            !syncDetails.isDuringSync &&
                                            syncDetails.tenantId === azureConnector[TENANT_ID.ID].value &&
                                            syncDetails.index === index
                                        }
                                        changedFunc={(fId, v) =>
                                            dispatchUpdateArray(NEW_AZURE_AD_CONNECTOR.ID, index, fId, v)
                                        }
                                        onSync={() => syncAzureConnectorPermissions(azureConnector, index)}
                                        isDuringSync={syncDetails.isDuringSync}
                                        disabled={disabled}
                                    />
                                </ArrayContainer>
                            </div>
                        );
                    })}
                    <Button
                        style={{ marginTop: `${azureConnectors.value.length === 0 ? '30px' : '0'}` }}
                        onClick={dispatchAddAzureConnector}
                        disabled={disabled}>
                        + Add Entra ID Connector
                    </Button>
                </div>
            )}

            {namingConventions && (
                <div className="mt-8">
                    <Label value="Match AD Accounts to Entra ID Accounts" />
                    <Description
                        value={
                            'Match Active Directory accounts to their corresponding Entra ID accounts using naming conventions.'
                        }
                        expendDescription={true}
                    />
                    <ConventionCreator
                        title=""
                        NAMING_CONVENTION_FIELD={AZURE_AD_NAMING_CONVENTIONS}
                        namingConventions={namingConventions}
                        addNewNamingConvention={dispatchAddNamingConvention}
                        updateConventionsArray={(index, id, fieldId, v) => dispatchUpdateArray(id, index, fieldId, v)}
                        removeItem={dispatchRemove}
                        filteredOptions={[CONDITIONS_OPTIONS.CONTAINS]}
                        disabled={disabled}
                    />
                </div>
            )}
        </>
    );
};

export default React.memo(AzureConnectors);
